'use client'

import { useEffect, useRef } from 'react'
import type { PreviewPlayerNavigation } from '@/components/assets-explorer/preview-player-bar'

type Options = {
  enabled: boolean
  hasPrevious: boolean
  hasNext: boolean
  navigation: React.RefObject<PreviewPlayerNavigation | null>
  previous: () => void
  next: () => void
  download: () => void
}

export function useAssetModalShortcuts(options: Options) {
  const latest = useRef(options)
  latest.current = options
  useEffect(() => {
    if (!options.enabled) return
    let held: { key: string; offset: number; controller: PreviewPlayerNavigation } | null = null
    const cancel = () => {
      held?.controller.cancel()
      held = null
    }
    const canHandle = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return false
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest('input, textarea, select, [role="slider"], [role="menu"], [role="listbox"]'))
      )
        return false
      const dialogs = document.querySelectorAll('[role="dialog"][data-state="open"]')
      return (
        dialogs.length > 0 && dialogs[dialogs.length - 1].getAttribute('data-slot') === 'asset-popup-dialog-content'
      )
    }
    const down = (event: KeyboardEvent) => {
      if (!canHandle(event)) return
      const current = latest.current
      if (event.key.toLowerCase() === 'd' && !event.shiftKey) {
        event.preventDefault()
        if (!event.repeat) current.download()
        return
      }
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return
      const offset = event.key === 'ArrowLeft' ? -1 : 1
      if (offset < 0 ? !current.hasPrevious : !current.hasNext) return
      event.preventDefault()
      if (event.shiftKey && current.navigation.current) {
        if (event.repeat || held) return
        held = { key: event.key, offset: offset * 10, controller: current.navigation.current }
        held.controller.begin(held.offset)
      } else if (!event.repeat) {
        if (offset < 0) current.previous()
        else current.next()
      }
    }
    const up = (event: KeyboardEvent) => {
      if (held && event.key === held.key) {
        event.preventDefault()
        const request = held
        held = null
        if (latest.current.navigation.current === request.controller) request.controller.release(request.offset)
        else request.controller.cancel()
      } else if (event.key === 'Escape') cancel()
    }
    document.addEventListener('keydown', down)
    document.addEventListener('keyup', up)
    window.addEventListener('blur', cancel)
    return () => {
      cancel()
      document.removeEventListener('keydown', down)
      document.removeEventListener('keyup', up)
      window.removeEventListener('blur', cancel)
    }
  }, [options.enabled])
}
